import {FeatureState} from '../page-data/payloads/header'
import type {FeatureStates} from '../page-data/payloads/header'
import {useLayoutRouteData} from '../page-data/loaders/use-layout-route-data'
import {pullRequestsLayoutRoute} from '../routes/layout-route'

import {makeSelectDataFromQueryData} from '@github-ui/react-core/future/main-query'
import type {LayoutRoutePayload} from '../routes/route-payload-types'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {getQueryClient} from '@github-ui/query-client'
import {useCallback, useSyncExternalStore} from 'react'

const EMPTY_FEATURE_STATES: Readonly<FeatureStates> = Object.freeze({})

export function useFeatureState(feature: keyof FeatureStates): FeatureState {
  const featureStates = useFeatureStates()
  return featureStates[feature] ?? FeatureState.Unavailable
}

export function useFeatureStates(): FeatureStates {
  const {featureStates} = useLayoutRouteData()
  return featureStates ?? EMPTY_FEATURE_STATES
}

const selectLayoutPayload = makeSelectDataFromQueryData<LayoutRoutePayload>()

export function useIsFeatureStateEnabled(feature: keyof FeatureStates): boolean {
  const {data} = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery', {
    select: raw => selectLayoutPayload(raw).featureStates?.[feature] === FeatureState.Enabled,
    notifyOnChangeProps: ['data'],
  })

  return data
}

// turns `["repo","pullRequestsLayoutRoute","/:owner/:repo/pull/:pr_number","mainQuery",{"pathname": "/github/github-ui/pull/12345/_layout"}]
// into `["repo","pullRequestsLayoutRoute","/:owner/:repo/pull/:pr_number","mainQuery"]`
const layoutMainQueryPrefix = pullRequestsLayoutRoute.queries.mainQuery.makeQueryKey().slice(0, 4)

// gets the overview feature state out of tanstack query without needing react-router loaded
// this is safe as the queryClient is constructed at runtime in getQueryClient
// uses useSyncExternalStore to subscribe to the query cache change without needing a specific key for the exact PR loaded
export function useIsOverviewEnabled(): boolean {
  const queryClient = getQueryClient()
  const subscribe = useCallback(
    (onChange: () => void) =>
      queryClient.getQueryCache().subscribe(event => {
        if (layoutMainQueryPrefix.every((part, index) => event.query.queryKey[index] === part)) onChange()
      }),
    [queryClient],
  )
  const getSnapshot = useCallback(
    () =>
      queryClient
        .getQueriesData({queryKey: layoutMainQueryPrefix, exact: false})
        // there should only be one query matching the layoutMainQueryPrefix in memory so this check is effecient
        .some(
          ([, raw]) => raw !== undefined && selectLayoutPayload(raw).featureStates?.overview === FeatureState.Enabled,
        ),
    [queryClient],
  )

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
